/**
 * Deterministic PRNG (xorshift128) shared by client and server.
 *
 * Item drops, map generation and AI jitter all pull from here, so a client
 * replaying a seed reproduces the authoritative outcome exactly.
 */
export class Rng {
  private a: number;
  private b: number;
  private c: number;
  private d: number;

  constructor(seed: number) {
    // splitmix32 the seed so nearby seeds diverge immediately.
    let s = seed >>> 0 || 0x9e3779b9;
    const next = () => {
      s = (s + 0x9e3779b9) >>> 0;
      let z = s;
      z = Math.imul(z ^ (z >>> 16), 0x21f0aaad) >>> 0;
      z = Math.imul(z ^ (z >>> 15), 0x735a2d97) >>> 0;
      return (z ^ (z >>> 15)) >>> 0;
    };
    this.a = next();
    this.b = next();
    this.c = next();
    this.d = next();
  }

  /** Raw 32-bit unsigned value. */
  next(): number {
    let t = this.b << 9;
    this.c ^= this.a;
    this.d ^= this.b;
    this.b ^= this.c;
    this.a ^= this.d;
    this.c ^= t;
    this.d = (this.d << 11) | (this.d >>> 21);
    return (Math.imul(this.a + this.d, 1) + this.b) >>> 0;
  }

  /** Float in [0, 1). */
  float(): number {
    return this.next() / 4294967296;
  }

  /** Integer in [0, n). */
  int(n: number): number {
    return n <= 0 ? 0 : this.next() % n;
  }

  /** Integer in [min, max] inclusive. */
  range(min: number, max: number): number {
    return min + this.int(max - min + 1);
  }

  pick<T>(items: readonly T[]): T {
    return items[this.int(items.length)];
  }

  /** Weighted pick from `[value, weight]` pairs. */
  weighted<T>(table: ReadonlyArray<readonly [T, number]>): T {
    let total = 0;
    for (const [, w] of table) total += w;
    let roll = this.float() * total;
    for (const [value, w] of table) {
      roll -= w;
      if (roll <= 0) return value;
    }
    return table[table.length - 1][0];
  }

  /** In-place Fisher-Yates. */
  shuffle<T>(items: T[]): T[] {
    for (let i = items.length - 1; i > 0; i--) {
      const j = this.int(i + 1);
      [items[i], items[j]] = [items[j], items[i]];
    }
    return items;
  }

  /** Snapshot of internal state, for serialising mid-match. */
  save(): [number, number, number, number] {
    return [this.a, this.b, this.c, this.d];
  }

  restore(state: readonly [number, number, number, number]): void {
    [this.a, this.b, this.c, this.d] = state;
  }
}
